/**
 * Dashboard API Service
 *
 * Centralized service for all dashboard widget data fetching.
 * Replaces mockDataService.ts with real API calls while providing
 * mock fallback during development.
 *
 * Uses ApiClient for authenticated HTTP calls with JWT token.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import { ApiClient } from './apiClient';
import { AuthService } from './authService';
import {
  OrderSummary,
  PickingStats,
  ReceivingStats,
  InventoryAlert,
  ShippingStats,
  OperatorActivity,
  ActivityEvent,
  KPIData,
  WarehouseCapacity,
  HourlyActivity,
} from '../components/warehouse/modules/wms-dashboard/types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Get the current user's company number from JWT token
 */
function getCompanyNo(): string {
  const userData = AuthService.getUserDataFromToken();
  if (!userData?.companyNo) {
    throw new Error('Ingen brukerdata tilgjengelig');
  }
  return userData.companyNo;
}

/**
 * Build the dashboard API base URL
 */
function getDashboardBaseUrl(): string {
  return `${API_BASE_URL}/${getCompanyNo()}/dashboard`;
}

/**
 * Generic fetch helper that handles response parsing and error handling.
 * Auto-reads warehouse from localStorage if no explicit warehouseId is provided,
 * so all dashboard widgets are automatically warehouse-filtered.
 */
async function fetchDashboardData<T>(endpoint: string, params?: Record<string, string>, warehouseId?: string): Promise<T> {
  const baseUrl = getDashboardBaseUrl();
  let url = `${baseUrl}${endpoint}`;

  const effectiveWarehouseId = warehouseId ?? localStorage.getItem('bizclick_warehouse') ?? undefined;
  const searchParams = new URLSearchParams(params || {});
  if (effectiveWarehouseId) {
    searchParams.append('warehouseId', effectiveWarehouseId);
  }
  if (searchParams.toString()) {
    url += `?${searchParams.toString()}`;
  }

  const response = await ApiClient.get(url);

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Manglende tilgang til dashboarddata');
    }
    if (response.status === 404) {
      throw new Error('Dashboard-endepunkt ikke funnet');
    }
    throw new Error(`Kunne ikke hente dashboarddata: ${response.statusText}`);
  }

  return response.json();
}

// ========================================================
// Dashboard API methods - one per widget/endpoint
// ========================================================

/**
 * Fetch order summary data
 */
export async function fetchOrderSummary(warehouseId?: string): Promise<OrderSummary> {
  return fetchDashboardData<OrderSummary>('/order-summary', undefined, warehouseId);
}

/**
 * Fetch picking statistics
 */
export async function fetchPickingStats(warehouseId?: string): Promise<PickingStats> {
  return fetchDashboardData<PickingStats>('/picking-stats', undefined, warehouseId);
}

/**
 * Fetch receiving statistics
 */
export async function fetchReceivingStats(warehouseId?: string): Promise<ReceivingStats> {
  return fetchDashboardData<ReceivingStats>('/receiving-stats', undefined, warehouseId);
}

/**
 * Fetch inventory alerts
 */
export async function fetchInventoryAlerts(warehouseId?: string): Promise<InventoryAlert[]> {
  return fetchDashboardData<InventoryAlert[]>('/inventory-alerts', undefined, warehouseId);
}

/**
 * Fetch shipping statistics
 */
export async function fetchShippingStats(warehouseId?: string): Promise<ShippingStats> {
  return fetchDashboardData<ShippingStats>('/shipping-stats', undefined, warehouseId);
}

/**
 * Fetch operator activity/status
 */
export async function fetchOperatorStatus(warehouseId?: string): Promise<OperatorActivity[]> {
  return fetchDashboardData<OperatorActivity[]>('/operator-status', undefined, warehouseId);
}

/**
 * Fetch recent activity events
 */
export async function fetchActivityFeed(warehouseId?: string): Promise<ActivityEvent[]> {
  return fetchDashboardData<ActivityEvent[]>('/activity-feed', undefined, warehouseId);
}

/**
 * Fetch KPI data
 */
export async function fetchKPIs(warehouseId?: string): Promise<KPIData[]> {
  return fetchDashboardData<KPIData[]>('/kpis', undefined, warehouseId);
}

/**
 * Fetch warehouse capacity data
 */
export async function fetchWarehouseCapacity(warehouseId?: string): Promise<WarehouseCapacity> {
  return fetchDashboardData<WarehouseCapacity>('/warehouse-capacity', undefined, warehouseId);
}

/**
 * Fetch hourly activity chart data
 */
export async function fetchHourlyActivity(warehouseId?: string): Promise<HourlyActivity[]> {
  return fetchDashboardData<HourlyActivity[]>('/hourly-activity', undefined, warehouseId);
}

// ========================================================
// New widget endpoints (Phase 2+)
// ========================================================

/**
 * Fetch leaderboard data
 */
export async function fetchLeaderboard(warehouseId?: string): Promise<LeaderboardEntry[]> {
  return fetchDashboardData<LeaderboardEntry[]>('/leaderboard', undefined, warehouseId);
}

/**
 * Fetch bottleneck detection data
 */
export async function fetchBottlenecks(warehouseId?: string): Promise<Bottleneck[]> {
  return fetchDashboardData<Bottleneck[]>('/bottlenecks', undefined, warehouseId);
}

/**
 * Fetch replenishment alerts
 */
export async function fetchReplenishmentAlerts(warehouseId?: string): Promise<ReplenishmentAlert[]> {
  return fetchDashboardData<ReplenishmentAlert[]>('/replenishment-alerts', undefined, warehouseId);
}

/**
 * Fetch pick accuracy data
 */
export async function fetchPickAccuracy(warehouseId?: string): Promise<PickAccuracyData> {
  return fetchDashboardData<PickAccuracyData>('/pick-accuracy', undefined, warehouseId);
}

/**
 * Fetch pick efficiency data
 */
export async function fetchPickEfficiency(warehouseId?: string): Promise<PickEfficiencyData> {
  return fetchDashboardData<PickEfficiencyData>('/pick-efficiency', undefined, warehouseId);
}

/**
 * Fetch zone utilization data
 */
export async function fetchZoneUtilization(warehouseId?: string): Promise<ZoneUtilization[]> {
  return fetchDashboardData<ZoneUtilization[]>('/zone-utilization', undefined, warehouseId);
}

/**
 * Fetch workforce balance data
 */
export async function fetchWorkforceBalance(warehouseId?: string): Promise<WorkforceBalance> {
  return fetchDashboardData<WorkforceBalance>('/workforce-balance', undefined, warehouseId);
}

/**
 * Fetch throughput time data
 */
export async function fetchThroughputTime(warehouseId?: string): Promise<ThroughputTimeData[]> {
  return fetchDashboardData<ThroughputTimeData[]>('/throughput-time', undefined, warehouseId);
}

/**
 * Fetch daily goal tracking data
 */
export async function fetchDailyGoals(warehouseId?: string): Promise<GoalProgress[]> {
  return fetchDashboardData<GoalProgress[]>('/daily-goals', undefined, warehouseId);
}

/**
 * Fetch workload forecast data
 */
export async function fetchWorkloadForecast(warehouseId?: string): Promise<WorkloadForecast[]> {
  return fetchDashboardData<WorkloadForecast[]>('/workload-forecast', undefined, warehouseId);
}

/**
 * Fetch order flow / funnel data
 */
export async function fetchOrderFlow(warehouseId?: string): Promise<OrderFlowStep[]> {
  return fetchDashboardData<OrderFlowStep[]>('/order-flow', undefined, warehouseId);
}

/**
 * Fetch comparison data (today vs yesterday)
 */
export async function fetchComparison(warehouseId?: string): Promise<ComparisonMetric[]> {
  return fetchDashboardData<ComparisonMetric[]>('/comparison', undefined, warehouseId);
}

/**
 * Fetch incoming deliveries
 */
export async function fetchIncomingDeliveries(warehouseId?: string): Promise<IncomingDelivery[]> {
  return fetchDashboardData<IncomingDelivery[]>('/incoming-deliveries', undefined, warehouseId);
}

/**
 * Fetch SLA monitoring data
 */
export async function fetchSLAStatus(warehouseId?: string): Promise<SLAStatus[]> {
  return fetchDashboardData<SLAStatus[]>('/sla-status', undefined, warehouseId);
}

/**
 * Fetch daily summary for executive view
 */
export async function fetchDailySummary(warehouseId?: string): Promise<DailySummary> {
  return fetchDashboardData<DailySummary>('/daily-summary', undefined, warehouseId);
}

/**
 * Fetch executive KPIs
 */
export async function fetchExecutiveKPIs(warehouseId?: string): Promise<ExecutiveKPI[]> {
  return fetchDashboardData<ExecutiveKPI[]>('/executive-kpis', undefined, warehouseId);
}

// ========================================================
// Types for new endpoints (will be moved to types.ts)
// ========================================================

export interface LeaderboardEntry {
  rank: number;
  operatorId: string;
  operatorName: string;
  linesProcessed: number;
  accuracy: number;
  avgTimePerLine: number;
  trend: 'up' | 'down' | 'stable';
}

export interface Bottleneck {
  id: string;
  type: 'zone_congestion' | 'slow_picking' | 'putaway_backlog' | 'shipping_delay';
  location: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  queueSize: number;
  avgWaitTime: number;
  recommendation: string;
}

export interface ReplenishmentAlert {
  id: string;
  itemNo: string;
  itemName: string;
  pickBin: string;
  currentQty: number;
  minQty: number;
  suggestedQty: number;
  sourceBin: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

export interface PickAccuracyData {
  accuracy: number;
  target: number;
  totalPicked: number;
  errors: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

export interface PickEfficiencyData {
  linesPerHour: number;
  target: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
  hourlyData: Array<{ hour: string; value: number }>;
}

export interface ZoneUtilization {
  zoneId: string;
  zoneName: string;
  totalBins: number;
  usedBins: number;
  utilizationPercent: number;
  activityLevel: 'cold' | 'warm' | 'hot';
  itemCount: number;
}

export interface WorkforceBalance {
  totalOperators: number;
  picking: number;
  receiving: number;
  putaway: number;
  transfer: number;
  idle: number;
}

export interface ThroughputTimeData {
  date: string;
  avgMinutes: number;
  target: number;
  orderCount: number;
}

export interface GoalProgress {
  id: string;
  name: string;
  current: number;
  target: number;
  unit: string;
  percent: number;
  icon: string;
}

export interface WorkloadForecast {
  hour: string;
  expectedOrders: number;
  expectedLines: number;
  requiredOperators: number;
  confidence: number;
}

export interface OrderFlowStep {
  step: string;
  label: string;
  count: number;
  color: string;
}

export interface ComparisonMetric {
  name: string;
  today: number;
  yesterday: number;
  unit: string;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
}

export interface IncomingDelivery {
  id: string;
  supplierName: string;
  orderNo: string;
  lineCount: number;
  expectedTime: string;
  status: 'expected' | 'arrived' | 'receiving' | 'completed';
}

export interface SLAStatus {
  customerId: string;
  customerName: string;
  slaTarget: number;
  actualTime: number;
  onTimePercent: number;
  ordersToday: number;
  status: 'on_track' | 'at_risk' | 'breached';
}

export interface DailySummary {
  ordersProcessed: number;
  linesPickedTotal: number;
  shipmentsCompleted: number;
  receivingCompleted: number;
  averageOrderTime: number;
  peakHour: string;
  topPerformer: string;
  highlights: string[];
}

export interface ExecutiveKPI {
  id: string;
  name: string;
  value: number;
  target: number;
  unit: string;
  description: string;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
  category: 'efficiency' | 'accuracy' | 'throughput' | 'utilization' | 'satisfaction';
}

// ========================================================
// Colleague Dashboard Types & API methods (Phase 5)
// ========================================================

export interface DashboardTargets {
  pickLinesDaily: number;
  receiveLinesDaily: number;
  shipmentsDaily: number;
}

export interface HourlyCount {
  hour: string;
  count: number;
}

export interface ShipmentByMethod {
  deliveryMethodNo: number | null;
  deliveryMethodName: string;
  shipmentCount: number;
}

export interface ShipmentSummary {
  totalShipments: number;
  totalCustomers: number;
  totalPackages: number;
  totalWeight: number;
  totalVolume: number;
  shipmentsTarget: number;
  byDeliveryMethod: ShipmentByMethod[];
}

export interface ActivityLog {
  hourlyPickLines: HourlyCount[];
  hourlyReceiveLines: HourlyCount[];
  totalPickLinesToday: number;
  totalReceiveLinesToday: number;
  pickLinesTarget: number;
  receiveLinesTarget: number;
  shipmentSummary: ShipmentSummary;
}

export interface RemainingByMethod {
  deliveryMethodNo: number | null;
  deliveryMethodName: string;
  notStarted: number;
  inProgress: number;
}

export interface RemainingOrders {
  totalNotStarted: number;
  totalInProgress: number;
  byDeliveryMethod: RemainingByMethod[];
}

export interface ReorderPointAlert {
  itemNo: string;
  description: string;
  location: string;
  balance: number;
  maxCapacity: number;
  reorderPoint: number;
}

export interface StockValue {
  totalValue: number;
  currency: string;
}

export interface OrderOverview {
  newOrders: number;
  inProgress: number;
  forPacking: number;
  completed: number;
}

/**
 * Fetch activity log (hourly pick/receive lines + shipments)
 */
export async function fetchActivityLog(warehouseId?: string): Promise<ActivityLog> {
  return fetchDashboardData<ActivityLog>('/activity-log', undefined, warehouseId);
}

/**
 * Fetch remaining pick orders by delivery method
 */
export async function fetchRemainingOrders(warehouseId?: string): Promise<RemainingOrders> {
  return fetchDashboardData<RemainingOrders>('/remaining-orders', undefined, warehouseId);
}

/**
 * Fetch reorder point alerts
 */
export async function fetchReorderPointAlerts(warehouseId?: string): Promise<ReorderPointAlert[]> {
  return fetchDashboardData<ReorderPointAlert[]>('/reorder-point-alerts', undefined, warehouseId);
}

/**
 * Fetch total stock value
 */
export async function fetchStockValue(warehouseId?: string): Promise<StockValue> {
  return fetchDashboardData<StockValue>('/stock-value', undefined, warehouseId);
}

/**
 * Fetch order overview (status groups 0/<45/45/60)
 */
export async function fetchOrderOverview(warehouseId?: string): Promise<OrderOverview> {
  return fetchDashboardData<OrderOverview>('/order-overview', undefined, warehouseId);
}

/**
 * Fetch dashboard targets
 */
export async function fetchDashboardTargets(warehouseId?: string): Promise<DashboardTargets> {
  return fetchDashboardData<DashboardTargets>('/targets', undefined, warehouseId);
}

/**
 * Save dashboard targets
 */
export async function updateDashboardTargets(targets: DashboardTargets, warehouseId?: string): Promise<void> {
  const baseUrl = getDashboardBaseUrl();
  const params = new URLSearchParams();
  if (warehouseId) params.append('warehouseId', warehouseId);
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const response = await ApiClient.put(`${baseUrl}/targets${queryString}`, targets);

  if (!response.ok) {
    throw new Error(`Kunne ikke lagre målsettinger: ${response.statusText}`);
  }
}
